import { useEffect, useRef, useState } from 'react'
import { Modal } from './Modal'
import { api, ApiError } from '../lib/api'
import { prepareImage } from '../lib/image'
import { useToast } from '../lib/toast'
import { FEEDBACK_CATEGORIES, type FeedbackCategory } from '@shared/types'
import { PHONE } from '../lib/media'

const MAX_IMAGES = 4
/** Screenshots stay legible; pantry photos shrink harder. */
const SCREENSHOT_EDGE = 1600

interface Props {
  /** Where they are, sent along so a bug report has its context. */
  page: string
  weekStart: string
  onClose: () => void
}

/**
 * The feedback form: an optional category, an open text box, and up to four
 * screenshots by drop, paste or picker. Everything else about the moment --
 * tab, week, viewport, browser -- is sent without asking.
 */
export function FeedbackModal({ page, weekStart, onClose }: Props) {
  const toast = useToast()
  const [category, setCategory] = useState<FeedbackCategory>('')
  const [message, setMessage] = useState('')
  const [images, setImages] = useState<{ blob: Blob; url: string; name: string }[]>([])
  const [dropping, setDropping] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    // Not on a phone. Focusing opens the keyboard, which costs over half the
    // screen before anyone has seen the form -- including the category
    // buttons, which are the part worth reading. Tapping the box is one tap,
    // and the sheet sits above the keyboard once it does.
    if (window.matchMedia(PHONE).matches) return
    textRef.current?.focus()
  }, [])

  useEffect(
    () => () => {
      for (const i of images) URL.revokeObjectURL(i.url)
    },
    // Only on unmount: the list's own updates revoke what they replace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  async function add(files: (File | Blob)[]) {
    setError(null)
    const room = MAX_IMAGES - images.length
    if (room <= 0) {
      setError(`Up to ${MAX_IMAGES} screenshots.`)
      return
    }
    const picked = files.filter((f) => f.type.startsWith('image/')).slice(0, room)
    if (picked.length === 0) {
      setError('That was not an image.')
      return
    }
    try {
      const prepared = await Promise.all(
        picked.map(async (f) => {
          const file = f instanceof File ? f : new File([f], 'pasted.png', { type: f.type })
          const blob = await prepareImage(file, SCREENSHOT_EDGE)
          return { blob, url: URL.createObjectURL(blob), name: file.name }
        }),
      )
      setImages((prev) => [...prev, ...prepared])
    } catch {
      setError('Could not read one of those images.')
    }
  }

  function remove(i: number) {
    setImages((prev) => {
      URL.revokeObjectURL(prev[i]!.url)
      return prev.filter((_, j) => j !== i)
    })
  }

  // Paste anywhere in the dialog: the fastest screenshot on a Mac is ⌘⇧4
  // then ⌘V, and on a phone the keyboard's own paste.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.items ?? [])]
        .filter((it) => it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => Boolean(f))
      if (files.length === 0) return
      e.preventDefault()
      void add(files)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  })

  async function submit() {
    const text = message.trim()
    if (!text || busy) return
    setBusy(true)
    setError(null)
    const form = new FormData()
    form.set('category', category)
    form.set('message', text)
    form.set('page', page)
    form.set('weekStart', weekStart)
    form.set('viewport', `${window.innerWidth}x${window.innerHeight}`)
    images.forEach((img, i) => form.append('images', img.blob, `screenshot-${i + 1}.${img.blob.type === 'image/png' ? 'png' : img.blob.type === 'image/webp' ? 'webp' : 'jpg'}`))
    try {
      await api.sendFeedback(form)
      toast.notify('Received — thank you. We read every one, and we’ll follow up if we need more.')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Send feedback"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <div className="spacer" />
          <button className="btn primary" onClick={submit} disabled={busy || !message.trim()}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div
        className={`feedback-form ${dropping ? 'over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setDropping(true)
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDropping(false)
          void add([...e.dataTransfer.files])
        }}
      >
        <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
          An idea, something that broke, something that made no sense — anything. It goes straight to the
          person who built this.
        </p>

        {error && <div className="banner error">{error}</div>}

        <div className="row wrap" role="group" aria-label="What kind">
          {FEEDBACK_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              className="btn sm"
              aria-pressed={category === c.value}
              onClick={() => setCategory(category === c.value ? '' : c.value)}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>

        <div className="field">
          <label htmlFor="fb-message">What’s on your mind?</label>
          <textarea
            id="fb-message"
            ref={textRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={4000}
            placeholder="The more specific the better, but one line is fine."
          />
        </div>

        <div className="field">
          <label>Screenshots</label>
          {images.length > 0 && (
            <div className="feedback-shots">
              {images.map((img, i) => (
                <div className="feedback-shot" key={img.url}>
                  <img src={img.url} alt={`Screenshot ${i + 1}`} />
                  <button type="button" className="x" aria-label={`Remove screenshot ${i + 1}`} onClick={() => remove(i)}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          {images.length < MAX_IMAGES && (
            <p className="dropzone-hint" style={{ margin: 0 }}>
              Drop images here, paste one, or{' '}
              <button type="button" className="linklike" onClick={() => fileInput.current?.click()}>
                choose files
              </button>
              . Up to {MAX_IMAGES}.
            </p>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              void add([...(e.target.files ?? [])])
              e.target.value = ''
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
